import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ProtectedRoute = ({ 
  children, 
  adminOnly = false, 
  requiredPermission = null,
  requiredAnyPermission = null,
  requiredAllPermissions = null,
  fallbackPath = "/announcements"
}) => {
  const { 
    isAuthenticated, 
    user, 
    loading, 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions, 
    isAdmin 
  } = useAuth();
  
  const location = useLocation();

  // Enhanced loading component - just spinner, no text
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the intended destination
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/login" 
        replace 
        state={{ from: location }} 
      />
    );
  }

  // Check admin-only routes
  if (adminOnly && !isAdmin) {
    return (
      <Navigate 
        to={fallbackPath} 
        replace 
        state={{ 
          from: location,
          message: "Admin access required" 
        }} 
      />
    );
  }

  // Check for single required permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <AccessDenied missingPermission={requiredPermission} />;
  }

  // Check for any of multiple permissions
  if (requiredAnyPermission && !hasAnyPermission(requiredAnyPermission)) {
    return <AccessDenied missingPermissions={requiredAnyPermission} />;
  }

  // Check for all of multiple permissions
  if (requiredAllPermissions && !hasAllPermissions(requiredAllPermissions)) {
    return <AccessDenied missingPermissions={requiredAllPermissions} />;
  }

  // Add debug information in development
  if (process.env.NODE_ENV === 'development') {
    console.log('ProtectedRoute - User:', user);
    console.log('ProtectedRoute - Permissions check passed');
  }

  return children;
};

// Enhanced Access Denied Component
const AccessDenied = ({ 
  missingPermission = null, 
  missingPermissions = null 
}) => {
  const { user } = useAuth();
  
  return (
    <div className="access-denied-container">
      <div className="access-denied-content">
        <div className="access-denied-icon">🚫</div>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        
        {/* Show specific missing permissions if provided */}
        {missingPermission && (
          <div className="missing-permission">
            <strong>Required Permission:</strong> {missingPermission}
          </div>
        )}
        
        {missingPermissions && Array.isArray(missingPermissions) && (
          <div className="missing-permissions">
            <strong>Required Permissions:</strong> {missingPermissions.join(', ')}
          </div>
        )}
        
        {/* Show user info for debugging */}
        {process.env.NODE_ENV === 'development' && user && (
          <div className="user-info-debug">
            <p><strong>User Role:</strong> {user.role}</p>
            <p><strong>User Permissions:</strong> {user.permissions?.join(', ') || 'None'}</p>
          </div>
        )}
        
        <div className="access-denied-actions">
          <button 
            onClick={() => window.history.back()}
            className="btn-go-back"
          >
            Go Back
          </button>
          <button 
            onClick={() => window.location.href = '/announcements'}
            className="btn-go-home"
          >
            Go to Home
          </button>
        </div>
      </div>
    </div>
  );
};

// Add CSS styles (you can also put these in your CSS file)
const styles = `
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: #f8f9fa;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .access-denied-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: #f8f9fa;
  }

  .access-denied-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    text-align: center;
    max-width: 500px;
    width: 90%;
  }

  .access-denied-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .missing-permission,
  .missing-permissions {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
    padding: 0.75rem;
    margin: 1rem 0;
    font-size: 0.9rem;
  }

  .user-info-debug {
    background: #e7f3ff;
    border: 1px solid #b3d9ff;
    border-radius: 4px;
    padding: 0.75rem;
    margin: 1rem 0;
    font-size: 0.8rem;
    text-align: left;
  }

  .access-denied-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-top: 1.5rem;
  }

  .btn-go-back,
  .btn-go-home {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
  }

  .btn-go-back {
    background: #6c757d;
    color: white;
  }

  .btn-go-back:hover {
    background: #5a6268;
  }

  .btn-go-home {
    background: #007bff;
    color: white;
  }

  .btn-go-home:hover {
    background: #0056b3;
  }
`;

// Inject styles (optional - you can also use CSS modules)
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default ProtectedRoute;